import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { scrollToSection, WHATSAPP_URL, track } from "@/lib/api";

const NAV = [
  { label: "Home", to: "/" },
  {
    label: "Study Destinations",
    items: [
      ["United Kingdom", "/study-in-uk"], ["USA", "/study-in-usa"], ["Canada", "/study-in-canada"],
      ["Australia", "/study-in-australia"], ["Germany", "/study-in-germany"], ["Ireland", "/study-in-ireland"],
      ["New Zealand", "/study-in-new-zealand"], ["France", "/study-in-france"], ["Italy", "/study-in-italy"],
      ["Romania", "/study-in-romania"], ["Netherlands", "/study-in-netherlands"], ["Switzerland", "/study-in-switzerland"],
      ["Spain", "/study-in-spain"], ["UAE", "/study-in-uae"], ["Singapore", "/study-in-singapore"],
      ["Malaysia", "/study-in-malaysia"], ["Mauritius", "/study-in-mauritius"], ["Explore All Destinations", "/destinations"],
    ],
  },
  { label: "Find a Course", to: "/courses" },
  { label: "Find a University", to: "/universities" },
  {
    label: "Services",
    items: [
      ["Education & Career Counselling", "/services"], ["Profile Assessment", "/services"],
      ["Country Selection", "/services"], ["Course Selection", "/services"],
      ["University Shortlisting", "/services"], ["Application Assistance", "/services"],
      ["Scholarship Guidance", "/scholarships"], ["Education Loan Guidance", "/education-loans"],
      ["Visa Guidance", "/visa-guidance"], ["Pre-Departure Support", "/services"],
      ["Find Your Study Path", "/find-your-path"],
    ],
  },
  { label: "Scholarships", to: "/scholarships" },
  { label: "Test Preparation", to: "/test-preparation" },
  { label: "Events", to: "/events" },
  {
    label: "Resources",
    items: [
      ["Insights & Blog", "/insights"], ["Global Education Updates", "/updates"],
      ["Application Checklist", "/checklist"], ["Cost Calculator", "/cost-calculator"],
      ["Intake Planner", "/intake-planner"], ["Visa Guides", "/visa-guidance"],
      ["Student Stories", "/student-stories"], ["FAQs", "/faq"],
    ],
  },
  {
    label: "About",
    items: [
      ["About Us", "/about"], ["Why Harwell", "/#why-harwell"], ["Our Approach", "/services"], ["Contact Us", "/contact"],
    ],
  },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const go = (to) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (to.startsWith("/#")) {
      if (location.pathname === "/") scrollToSection(to.slice(1));
      else navigate(to);
    } else {
      navigate(to);
    }
  };

  return (
    <header data-testid="harwell-header-nav"
      className={`fixed top-0 inset-x-0 z-50 bg-white transition-all duration-300 ${
        scrolled ? "border-b border-[#E6EBF1] shadow-md" : "border-b border-slate-100"
      }`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[88px]">
          <Link to="/" data-testid="harwell-header-logo-slot" className="flex items-center shrink-0 mr-6">
            <img src="/harwell-logo-transparent.png" alt="Harwell Global Education" className="h-14 w-auto object-contain" />
          </Link>

          <nav className="hidden 2xl:flex items-center">
            {NAV.map((m) =>
              m.items ? (
                <div key={m.label} className="relative group" data-testid={`nav-menu-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <button className="flex items-center gap-1 px-2.5 py-2 text-[12.5px] font-semibold text-[#172033] hover:text-[#1E40AF] transition-colors whitespace-nowrap">
                    {m.label}
                    <ChevronDown className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform" />
                  </button>
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-1 group-hover:translate-y-0">
                    <div data-lenis-prevent className={`bg-white rounded-xl border border-[#E6EBF1] shadow-xl shadow-slate-900/8 py-2 ${
                      m.label === "Study Destinations" ? "w-[540px] grid grid-cols-2" : "w-64 max-h-[70vh] overflow-y-auto"
                    }`}>
                      {m.items.map(([label, to]) => (
                        <a key={label} href={to} onClick={go(to)} data-testid={`nav-item-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="block px-5 py-2.5 text-[13px] font-medium text-[#5F6B7A] hover:text-[#1E40AF] hover:bg-[#F7F9FC] transition-colors">
                          {label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <a key={m.label} href={m.to} onClick={go(m.to)} data-testid={`nav-link-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="px-2.5 py-2 text-[12.5px] font-semibold text-[#172033] hover:text-[#1E40AF] transition-colors whitespace-nowrap">
                  {m.label}
                </a>
              )
            )}
          </nav>

          <div className="hidden 2xl:flex items-center gap-3 ml-4">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { source: "header" })}
              data-testid="nav-btn-whatsapp-expert"
              className="px-4 py-2.5 rounded-full text-[12px] font-bold text-[#059669] border border-[#059669]/40 hover:bg-emerald-50 transition-colors whitespace-nowrap">
              WHATSAPP AN EXPERT
            </a>
            <button onClick={go("/#assessment")} data-testid="nav-btn-profile-assessment"
              className="px-5 py-2.5 rounded-full text-[12px] font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-lg shadow-blue-900/20 active:scale-[0.98] transition-all whitespace-nowrap">
              GET FREE PROFILE ASSESSMENT
            </button>
          </div>

          <div className="hidden xl:flex 2xl:hidden items-center gap-3 ml-4">
            <button onClick={go("/#assessment")} data-testid="nav-btn-profile-assessment-compact"
              className="px-4 py-2.5 rounded-full text-[12px] font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-lg shadow-blue-900/20 active:scale-[0.98] transition-all whitespace-nowrap">
              PROFILE ASSESSMENT
            </button>
            <button data-testid="nav-mobile-menu-btn" onClick={() => setOpen(!open)}
              className="p-2 rounded-lg text-slate-800 hover:bg-slate-100" aria-label="Menu">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          <button data-testid="nav-mobile-menu-btn-sm" onClick={() => setOpen(!open)}
            className="xl:hidden p-2 rounded-lg text-slate-800 hover:bg-slate-100" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="2xl:hidden bg-white border-t border-[#E6EBF1] shadow-xl max-h-[75vh] overflow-y-auto" data-lenis-prevent>
          <nav className="px-5 py-4">
            {NAV.map((m) =>
              m.items ? (
                <div key={m.label} className="border-b border-slate-100">
                  <button onClick={() => setMobileSection(mobileSection === m.label ? null : m.label)}
                    className="w-full flex items-center justify-between py-3.5 text-sm font-bold text-[#172033]">
                    {m.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileSection === m.label ? "rotate-180" : ""}`} />
                  </button>
                  {mobileSection === m.label && (
                    <div className="pb-3 pl-3">
                      {m.items.map(([label, to]) => (
                        <a key={label} href={to} onClick={go(to)} className="block py-2.5 text-sm text-[#5F6B7A] hover:text-[#1E40AF]">
                          {label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a key={m.label} href={m.to} onClick={go(m.to)}
                  className="block py-3.5 text-sm font-bold text-[#172033] border-b border-slate-100">
                  {m.label}
                </a>
              )
            )}
            <button onClick={go("/contact")} data-testid="mobile-nav-btn-free-counselling"
              className="mt-4 w-full py-3.5 rounded-full text-sm font-bold text-white bg-[#1E40AF]">
              GET FREE PROFILE ASSESSMENT
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
